// @flow strict

import * as React from 'react';
import cx from 'classnames';
import styles from './styles.scss';
import BlockWrapper from '../BlockWrapper';
import { Button } from '../../internal/Button';
import LazyLoadImage from '../../internal/LazyLoadImage';

type CtaLink = {|
  text: string,
  href: string,
|};

type BackgroundColors = 'Black' | 'White' | 'Silver' | 'Charcoal' | 'Green' | 'Image';

type Block = {|
  background?: BackgroundColors,
  imageLocation?: 'Bottom' | 'BottomLeft' | 'BottomRight' | 'BottomMiddle' | 'Fill',
  text?: string,
  image: string,
  titleImage: string,
  titleAlt: string,
  ctaLinks: Array<CtaLink>,
  theme?: 'Light' | 'Dark',
  double?: boolean,
|};

type PropTypes = {|
  blocks: Array<Block>,
|};

const ImageCta = ({ blocks }: PropTypes) => (
  <BlockWrapper>
    {blocks.map(block => (
      <Item double={blocks.length > 1} {...block} />
    ))}
  </BlockWrapper>
);

const findBestTheme = (background?: BackgroundColors) => {
  switch (background) {
    case 'Black':
      return 'Light';
    default: {
      return 'Dark';
    }
  }
};
const findButtonVariant = (background: BackgroundColors, index: number) => {
  switch (background) {
    case 'Black':
    case 'Charcoal':
      return index % 2 === 0 ? 'White' : 'Citrus';

    case 'Green':
      return index % 2 === 0 ? 'Black' : 'DefaultInverse';

    case 'Silver':
    case 'White':
      return index % 2 === 0 ? 'Black' : 'Citrus';

    case 'Image':
      return index % 2 === 0 ? 'Argon' : 'Black';

    default:
      return index % 2 === 0 ? 'Black' : 'Citrus';
  }
};

const Item = ({
  background = 'Silver',
  imageLocation = background === 'Image' ? 'BottomMiddle' : 'Bottom',
  text,
  image,
  titleImage,
  titleAlt,
  theme = findBestTheme(background),
  ctaLinks,
  double = false,
}: Block) => (
  <div
    className={cx(
      styles.blockWrapper,
      background === 'Black' && styles.black,
      background === 'Silver' && styles.silver,
      background === 'Green' && styles.green,
      background === 'Charcoal' && styles.charcoal,
      background === 'White' && styles.white,
    )}
  >
    <div
      className={cx(
        styles.block,
        double ? styles.blockTwo : styles.blockOne,
        background === 'Black' && styles.black,
        background === 'Silver' && styles.silver,
        background === 'Green' && styles.green,
        background === 'Charcoal' && styles.charcoal,
        background === 'White' && styles.white,
        background !== 'Image' && imageLocation === 'Bottom' && styles.imageBottom,
        background !== 'Image' && imageLocation === 'BottomRight' && styles.imageBottomRight,
        background !== 'Image' && imageLocation === 'BottomMiddle' && styles.imageBottomMiddle,
        background === 'Image' && imageLocation === 'BottomMiddle' && styles.imageFillMiddle,
        background === 'Image' && imageLocation === 'BottomLeft' && styles.imageFillLeft,
        background === 'Image' && imageLocation === 'BottomRight' && styles.imageFillRight,
      )}
      style={{
        backgroundImage: `url(${image}?height=690)`,
      }}
    >
      <h4 className={cx(styles.titleText)}>
        <LazyLoadImage
          src={`${titleImage}?height=60`}
          alt={titleAlt}
          className={styles.titleImage}
          defaultSize={{ height: 30, width: 125 }}
        />
        <span className={cx(styles.visuallyhidden)}>{titleAlt}</span>
      </h4>
      {text && (
        <p
          className={cx(
            styles.blockText,
            theme === 'Light' && styles.whiteText,
            theme === 'Dark' && styles.blackText,
          )}
        >
          {text}
        </p>
      )}
      {ctaLinks.length > 0 && (
        <div className={styles.links}>
          {ctaLinks.map((ctaLink, index) => (
            <Button
              onClick={() => window.open(ctaLink.href, '_self')}
              variant={findButtonVariant(background, index)}
            >
              {ctaLink.text}
            </Button>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default ImageCta;
